/**
 * Dictation orchestration hook.
 *
 * Sits at the input layer; touches NOTHING in the agent pipeline or the
 * typed tool contract. The hook is the seam between the React state machine
 * and two SYSTEM BOUNDARIES — the browser `MediaRecorder` and the
 * `/transcribe` `fetch` — both injected as ports so the orchestration is
 * unit-testable against deterministic fakes (the locked test-depth decision;
 * the real recorder + network are verified by manual demo + HITL review).
 *
 * Lifecycle: `start` → recording → `stop` → transcribing → `onText(text)`;
 * `cancel` ends recording without transcribing; an error surfaces via
 * `error` and `onText` is never invoked on a failure.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface DictationRecorder {
    /** Request mic permission + begin recording. Resolves when recording. */
    start(): Promise<void>;
    /** Stop recording; resolves with the captured audio + its mime type. */
    stop(): Promise<{ readonly audio: Uint8Array; readonly mimeType: string }>;
    /** Abandon the current recording; release the mic; no audio returned. */
    cancel(): void;
}

export interface UseDictationDeps {
    /** Construct a fresh recorder per dictation (the production factory
     *  wraps the browser `MediaRecorder`; tests pass a deterministic fake). */
    readonly recorderFactory: () => DictationRecorder;
    /** POST audio to `/transcribe`; production wires this to `fetch`. */
    readonly transcribe: (
        audio: Uint8Array,
        mimeType: string,
        signal?: AbortSignal
    ) => Promise<{ readonly text: string }>;
    /** Successful transcription — caller decides what to do with the draft. */
    readonly onText: (text: string) => void;
}

export interface UseDictationApi {
    readonly recording: boolean;
    readonly busy: boolean;
    readonly error: string | undefined;
    start(): Promise<void>;
    stop(): Promise<void>;
    cancel(): void;
}

export function useDictation(deps: UseDictationDeps): UseDictationApi {
    const [recording, setRecording] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const recorderRef = useRef<DictationRecorder | null>(null);
    // Aborts the in-flight transcription on cancel/unmount. Internal to the
    // hook — never exposed.
    const transcribeAbortRef = useRef<AbortController | null>(null);
    // Cleared on teardown so a late transcription cannot call back onText.
    const aliveRef = useRef(true);

    useEffect(() => {
        return () => {
            aliveRef.current = false;
            transcribeAbortRef.current?.abort();
            // Release the mic if recording is still live at teardown; the
            // recorder's MediaStream + buffer would otherwise outlive the hook.
            recorderRef.current?.cancel();
            recorderRef.current = null;
        };
    }, []);

    const start = useCallback(async (): Promise<void> => {
        if (recorderRef.current) {
            return;
        }
        setError(undefined);
        const recorder = deps.recorderFactory();
        recorderRef.current = recorder;
        try {
            await recorder.start();
            setRecording(true);
        } catch (e) {
            recorderRef.current = null;
            setError(e instanceof Error ? e.message : 'recording failed');
        }
    }, [deps]);

    const stop = useCallback(async (): Promise<void> => {
        const recorder = recorderRef.current;
        if (!recorder) {
            return;
        }
        recorderRef.current = null;
        setRecording(false);
        setBusy(true);
        const abort = new AbortController();
        transcribeAbortRef.current = abort;
        try {
            const { audio, mimeType } = await recorder.stop();
            const { text } = await deps.transcribe(
                audio,
                mimeType,
                abort.signal
            );
            if (abort.signal.aborted || !aliveRef.current) {
                return;
            }
            deps.onText(text);
        } catch (e) {
            if (abort.signal.aborted || !aliveRef.current) {
                return;
            }
            setError(e instanceof Error ? e.message : 'transcription failed');
        } finally {
            if (transcribeAbortRef.current === abort) {
                transcribeAbortRef.current = null;
            }
            setBusy(false);
        }
    }, [deps]);

    const cancel = useCallback((): void => {
        transcribeAbortRef.current?.abort();
        transcribeAbortRef.current = null;
        const recorder = recorderRef.current;
        if (!recorder) {
            setBusy(false);
            return;
        }
        recorderRef.current = null;
        recorder.cancel();
        setRecording(false);
    }, []);

    return { recording, busy, error, start, stop, cancel };
}
